import { useEffect, useState } from 'react';
import type { CSSProperties, MouseEvent } from 'react';
import { assetUrl } from '../lib/api';
import { useAssetUrl } from '../lib/useAssetUrl';

/**
 * ApprovalPreview — renders an approval's preview asset as the actual thing.
 *
 * - .html assets (Milo campaigns, later Leo landing pages) render inline in a
 *   fully sandboxed iframe (sandbox="" — no scripts, unique origin) with
 *   expand/collapse, a "View code" panel (copyable source, Vault-style tick),
 *   and an annotation mode: click to drop numbered pins, note each one, and
 *   the parent compiles them into a request-changes comment.
 * - Everything else keeps the img / video / audio behavior the page had.
 *
 * Pins live in the PARENT (keyed per asset) so "Request changes" can read
 * them; this component just renders + mutates them via onPinsChange.
 */

export interface Pin {
  /** % coordinates of the click within the visible preview frame. */
  x: number;
  y: number;
  note: string;
}

export function isHtmlAsset(key: string): boolean {
  return /\.html?$/i.test(key);
}

/** Rough human region for a pin — "top-left", "middle-center", "bottom-right". */
export function regionName(x: number, y: number): string {
  const vert = y < 33.4 ? 'top' : y < 66.7 ? 'middle' : 'bottom';
  const horiz = x < 33.4 ? 'left' : x < 66.7 ? 'center' : 'right';
  return `${vert}-${horiz}`;
}

/** "Pin 1 (top-left area): make the headline bigger" — one line per pin. */
export function compilePins(pins: Pin[]): string {
  return pins
    .map((p, i) => `Pin ${i + 1} (${regionName(p.x, p.y)} area): ${p.note.trim() || '(see pin)'}`)
    .join('\n');
}

function assetKind(key: string): 'html' | 'video' | 'audio' | 'text' | 'image' {
  if (isHtmlAsset(key)) return 'html';
  if (/\.(mp4|webm|mov|m4v)$/i.test(key)) return 'video';
  if (/\.(mp3|wav|m4a|ogg|aac|flac)$/i.test(key)) return 'audio';
  if (/\.(md|markdown|txt)$/i.test(key)) return 'text';
  return 'image';
}

/* ------------------------------------------------------------------ styles */

const frame: CSSProperties = {
  width: '100%',
  borderRadius: 'var(--r-ctl)',
  border: '1px solid var(--line)',
  background: 'var(--panel-2)',
};

const pinDot: CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  border: '1px solid var(--amber)',
  background: 'var(--panel)',
  color: 'var(--amber)',
  fontFamily: 'var(--mono)',
  fontSize: 11,
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 1px 5px rgba(0,0,0,.35)',
  flexShrink: 0,
};

const noteInput: CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: '8px 10px',
  borderRadius: 'var(--r-ctl)',
  border: '1px solid var(--line-2)',
  background: 'var(--panel-2)',
  color: 'var(--text)',
  fontFamily: 'var(--font)',
  fontSize: 13,
};

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/* ----------------------------------------------------- non-HTML (existing) */

function MediaPreview({ assetKey }: { assetKey: string }) {
  const url = useAssetUrl(assetKey);
  const kind = assetKind(assetKey);
  if (!url) {
    // token still resolving — hold the slot so the grid doesn't jump
    if (kind === 'audio') return <div style={{ ...frame, gridColumn: '1 / -1', height: 40 }} aria-hidden />;
    return <div style={{ ...frame, aspectRatio: '4 / 3' }} aria-hidden />;
  }
  if (kind === 'video') return <video src={url} controls preload="metadata" style={frame} />;
  if (kind === 'audio') return <audio src={url} controls preload="metadata" style={{ width: '100%', gridColumn: '1 / -1' }} />;
  return <img src={url} alt="" loading="lazy" style={{ ...frame, aspectRatio: '4 / 3', objectFit: 'cover' }} />;
}

/** .md / .txt assets (e.g. Milo's send calendar) — fetch and show the text itself. */
function TextPreview({ assetKey }: { assetKey: string }) {
  const url = useAssetUrl(assetKey);
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!url) return;
    let alive = true;
    setText(null);
    setFailed(false);
    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((t) => {
        if (alive) setText(t);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [url]);

  if (failed) {
    return (
      <div style={{ ...frame, padding: 14, fontSize: 12.5, color: 'var(--dim)' }}>
        Couldn't load this preview just now — refresh to try again.
      </div>
    );
  }
  if (text === null) return <div style={{ ...frame, aspectRatio: '4 / 3' }} aria-hidden />;
  return (
    <pre
      style={{
        ...frame,
        margin: 0,
        padding: 14,
        maxHeight: 480,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily: 'var(--mono)',
        fontSize: 12,
        lineHeight: 1.55,
        color: 'var(--text)',
      }}
    >
      {text}
    </pre>
  );
}

/* ------------------------------------------------------------- HTML canvas */

interface HtmlPreviewProps {
  assetKey: string;
  pins: Pin[];
  onPinsChange: (pins: Pin[]) => void;
}

function HtmlPreview({ assetKey, pins, onPinsChange }: HtmlPreviewProps) {
  const url = useAssetUrl(assetKey);
  const label = /email|campaign/i.test(assetKey) ? 'Email preview' : 'Page preview';

  const [expanded, setExpanded] = useState(false);
  const [annotating, setAnnotating] = useState(false);

  const [codeOpen, setCodeOpen] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const height = expanded ? 'min(78vh, 900px)' : '420px';

  async function toggleCode() {
    if (codeOpen) {
      setCodeOpen(false);
      return;
    }
    setCodeOpen(true);
    if (code !== null || codeLoading) return;
    setCodeLoading(true);
    setCodeErr(null);
    try {
      // The tokened /assets URL carries its own auth — no header needed.
      const res = await fetch(await assetUrl(assetKey));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCode(await res.text());
    } catch (e) {
      setCodeErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCodeLoading(false);
    }
  }

  function copyCode() {
    if (code == null) return;
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        /* clipboard unavailable — nothing sensible to do */
      });
  }

  function dropPin(e: MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
    onPinsChange([...pins, { x, y, note: '' }]);
  }

  function removePin(idx: number) {
    onPinsChange(pins.filter((_, i) => i !== idx));
  }

  function setNote(idx: number, note: string) {
    onPinsChange(pins.map((p, i) => (i === idx ? { ...p, note } : p)));
  }

  return (
    <div style={{ marginBottom: 14 }}>
      {/* label + controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <div className="t-label" style={{ marginRight: 'auto' }}>{label}</div>
        <button
          type="button"
          className="btn btn-line sm"
          onClick={() => setAnnotating((v) => !v)}
          style={annotating ? { color: 'var(--amber)', borderColor: 'rgba(var(--amber-rgb),.4)' } : undefined}
          aria-pressed={annotating}
        >
          {annotating ? 'Done annotating' : 'Annotate'}
        </button>
        <button type="button" className="btn btn-line sm" onClick={toggleCode} aria-expanded={codeOpen}>
          {codeOpen ? 'Hide code' : 'View code'}
        </button>
        <button type="button" className="btn btn-line sm" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {/* the rendered thing */}
      <div style={{ position: 'relative', borderRadius: 'var(--r-ctl)', border: '1px solid var(--line)', overflow: 'hidden' }}>
        {url ? (
          <iframe
            src={url}
            title={label}
            sandbox=""
            style={{ display: 'block', width: '100%', height, border: 0, background: '#fff' }}
          />
        ) : (
          <div
            className="mono faint"
            style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, background: 'var(--panel-2)' }}
          >
            loading preview…
          </div>
        )}

        {/* pins + click layer (transparent; intercepts clicks only while annotating) */}
        <div
          onClick={annotating ? dropPin : undefined}
          style={{
            position: 'absolute',
            inset: 0,
            cursor: annotating ? 'crosshair' : 'default',
            pointerEvents: annotating ? 'auto' : 'none',
          }}
          aria-hidden={!annotating}
        >
          {pins.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (annotating) removePin(i);
              }}
              title={annotating ? 'Remove pin' : `Pin ${i + 1}`}
              aria-label={annotating ? `Remove pin ${i + 1}` : `Pin ${i + 1}`}
              style={{
                ...pinDot,
                position: 'absolute',
                left: `${p.x}%`,
                top: `${p.y}%`,
                transform: 'translate(-50%,-50%)',
                cursor: annotating ? 'pointer' : 'default',
                pointerEvents: annotating ? 'auto' : 'none',
              }}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>

      {annotating && (
        <p className="mono faint" style={{ fontSize: 11, marginTop: 6 }}>
          click the preview to drop a pin · click a pin to remove it
        </p>
      )}

      {/* pin notes */}
      {pins.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
          {pins.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={pinDot} aria-hidden>
                {i + 1}
              </span>
              <input
                value={p.note}
                onChange={(e) => setNote(i, e.target.value)}
                placeholder={`What should change here? (${regionName(p.x, p.y)} area)`}
                style={noteInput}
                aria-label={`Note for pin ${i + 1}`}
              />
              <button
                type="button"
                className="btn btn-ghost sm"
                onClick={() => removePin(i)}
                title={`Remove pin ${i + 1}`}
                aria-label={`Remove pin ${i + 1}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* source code panel */}
      {codeOpen && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div className="t-label" style={{ marginRight: 'auto' }}>Source code</div>
            <button
              type="button"
              className="btn btn-line sm"
              onClick={copyCode}
              disabled={code == null}
              title="Copy code"
              style={copied ? { color: 'var(--mint)', borderColor: 'rgba(var(--mint-rgb),.4)' } : undefined}
            >
              {copied ? (
                <>
                  <CheckIcon />
                  Copied
                </>
              ) : (
                'Copy'
              )}
            </button>
          </div>
          {codeLoading && (
            <p className="mono faint" style={{ fontSize: 11 }}>
              fetching code…
            </p>
          )}
          {codeErr && (
            <p className="mono" style={{ fontSize: 11.5, color: 'var(--coral)' }}>
              couldn't fetch code — {codeErr}
            </p>
          )}
          {code !== null && (
            <pre
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 11.5,
                lineHeight: 1.55,
                color: 'var(--text)',
                background: 'var(--panel-2)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-ctl)',
                padding: '10px 12px',
                maxHeight: 320,
                overflow: 'auto',
                whiteSpace: 'pre',
              }}
            >
              {code}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- exports */

interface ApprovalPreviewProps {
  assetKey: string;
  /** Annotation pins for this asset (HTML only) — owned by the parent. */
  pins?: Pin[];
  onPinsChange?: (pins: Pin[]) => void;
}

export function ApprovalPreview({ assetKey, pins, onPinsChange }: ApprovalPreviewProps) {
  if (isHtmlAsset(assetKey)) {
    return <HtmlPreview assetKey={assetKey} pins={pins ?? []} onPinsChange={onPinsChange ?? (() => {})} />;
  }
  if (assetKind(assetKey) === 'text') return <TextPreview assetKey={assetKey} />;
  return <MediaPreview assetKey={assetKey} />;
}
